import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import { Row, Col, Form, Spinner } from 'react-bootstrap';

// NEW IMPORT FOR GLOBAL PHONE VALIDATION
import { isValidPhoneNumber } from 'libphonenumber-js';

import axiosInstance from '../../utils/axios';
import SoilDoctorLogo from '../../assets/Images/SoilDoctorLogo.png';
import LoginImage from '../../assets/Images/LoginImage.png';
import '../../styles/auth.css';

import { 
  login as partnerLogin, 
  resetPartnerAuth 
} from '../../store/features/partnerSlice';

import { 
  login as supportLogin, 
  resetSupportAuth 
} from '../../store/features/supportSlice';

import { 
  login as fieldOfficerLogin, 
  resetFieldOfficerAuth 
} from '../../store/features/fieldOfficersSlice';

import { 
  login as farmerLogin, 
  resetFarmerAuth 
} from '../../store/features/farmerSlice';

import { 
  selectCurrentRole, 
  selectRoleLoading 
} from '../../store/features/roleSlice';

// --- Validation Schemas ---

const emailPassSchema = Yup.object({
  email: Yup.string()
    .trim()
    .required('Email is required')
    .email('Invalid Email ID'),
  password: Yup.string()
    .trim()
    .required('Password is required'),
});

// --- UPDATED OTP SCHEMA ---
const otpSchema = Yup.object({
  phone: Yup.string()
    .required('Phone is required')
    .test('is-valid-phone', 'Invalid phone number', (value) => {
      if (!value) return false;
      return isValidPhoneNumber(value, 'IN');
    }),
});

const generateClassName = (errors, touched, fieldName) => {
  const hasError = errors[fieldName] && touched[fieldName];
  const isSuccess = touched[fieldName];
  return `form-control ${
    hasError
      ? 'border-2 border-danger'
      : isSuccess
      ? 'border-2 border-success'
      : ''
  }`;
};

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [userType, setUserType] = useState('support');
  const [otpSent, setOtpSent] = useState(false);
  const [description, setDescription] = useState('');
  const [otpError, setOtpError] = useState('');

  const currentRole = useSelector(selectCurrentRole);
  const isRoleFetching = useSelector(selectRoleLoading);

  const { 
    status: partnerStatus, 
    error: partnerError 
  } = useSelector((state) => state.partner);

  const { 
    loginStatus: supportStatus, 
    error: supportError 
  } = useSelector((state) => state.support);

  const { 
    status: fieldOfficerStatus, 
    error: fieldOfficerReduxError 
  } = useSelector((state) => state.fieldOfficer);

  const { 
    status: farmerStatus, 
    error: farmerReduxError 
  } = useSelector((state) => state.farmer);

  useEffect(() => {
    if (currentRole && !isRoleFetching) {
      navigate('/');
    }
  }, [currentRole, isRoleFetching, navigate]);

  useEffect(() => {
    dispatch(resetSupportAuth());
    dispatch(resetFieldOfficerAuth());
    dispatch(resetFarmerAuth());
    dispatch(resetPartnerAuth());
  }, [dispatch]);

  const handleUserTypeChange = (type) => {
    setUserType(type);
    setOtpSent(false);
    setDescription('');
    setOtpError('');
  };

  const isLoading =
    isRoleFetching ||
    (userType === 'partner' && partnerStatus === 'loading') ||
    (userType === 'support' && supportStatus === 'loading') ||
    (userType === 'field_officer' && fieldOfficerStatus === 'loading') ||
    (userType === 'farmer' && farmerStatus === 'loading');

  let currentError = null;
  if (userType === 'partner') currentError = partnerError;
  else if (userType === 'support') currentError = supportError;
  else if (userType === 'field_officer') currentError = fieldOfficerReduxError || otpError;
  else if (userType === 'farmer') currentError = farmerReduxError || otpError;

  return (
    <div className='login-container m-0'>
      {/* Left Panel */}
      <div className='login-left'>
        <div className='logo'>
          <img src={SoilDoctorLogo} alt='Logo' width='250px' />
        </div>
        <div className='left-content'>
          <h1 className='fw-bolder pt-5'>Welcome to Soil Doctor!</h1>
          <img src={LoginImage} alt='Background' className='responsive-image' />
          <figure className='w-75'>
            <blockquote className='blockquote'>
              <h3>
                To forget how to dig the earth and to tend the soil is to forget
                ourselves.
              </h3>
            </blockquote>
            <figcaption className='blockquote-footer'>
              <cite title='Source Title'>Mohandas K. Gandhi</cite>
            </figcaption>
          </figure>
        </div>
      </div>

      {/* Right Panel */}
      <div className='login-right'>
        <div className='login-form'>
          <h1 className='mb-4'>Sign in to Soil Doctor</h1>

          {/* Radio Buttons */}
          <div className='mb-4 d-flex gap-3 flex-wrap'>
            <Form.Check
              type='radio'
              id='support-radio'
              label='Operations'
              name='userType'
              checked={userType === 'support'}
              onChange={() => handleUserTypeChange('support')}
              className='fw-bold'
            />
            <Form.Check
              type='radio'
              id='partner-radio'
              label='Partner'
              name='userType'
              checked={userType === 'partner'}
              onChange={() => handleUserTypeChange('partner')}
              className='fw-bold'
            />
            <Form.Check
              type='radio'
              id='fo-radio'
              label='Field Officer'
              name='userType'
              checked={userType === 'field_officer'}
              onChange={() => handleUserTypeChange('field_officer')}
              className='fw-bold'
            />
            <Form.Check
              type='radio'
              id='farmer-radio'
              label='Farmer'
              name='userType'
              checked={userType === 'farmer'}
              onChange={() => handleUserTypeChange('farmer')}
              className='fw-bold'
            />
          </div>

          {(userType === 'partner' || userType === 'support') ? (
            
            /* --- Email/Password Form --- */
            <Formik
              initialValues={{ email: '', password: '' }}
              validationSchema={emailPassSchema}
              onSubmit={(values, { setSubmitting }) => {
                if (userType === 'partner') {
                  dispatch(partnerLogin({ email: values.email, password: values.password }));
                } else {
                  dispatch(supportLogin(values));
                }
                setSubmitting(false);
              }}
            >
              {({ values, handleChange, handleSubmit, setFieldTouched, errors, touched }) => (
                <Form onSubmit={handleSubmit} onChange={handleChange}>
                  <Row className='mb-3'>
                    <Col md={12}>
                      <div className='form-floating mb-3'>
                        <Field
                          type='email'
                          name='email'
                          id='email'
                          value={values.email}
                          placeholder='Enter Registered Email'
                          className={generateClassName(errors, touched, 'email')}
                          onChange={handleChange}
                          onBlur={() => setFieldTouched('email', true)}
                        />
                        <Form.Label htmlFor='email'>
                          Enter Registered Email <span className='text-danger'>*</span>
                        </Form.Label>
                        {errors.email && touched.email && (
                          <div className='form_errors text-danger mt-1'>&#9888; {errors.email}</div>
                        )}
                      </div>
                    </Col>
                    <Col md={12}>
                      <div className='form-floating'>
                        <Field
                          type='password'
                          name='password'
                          id='password'
                          value={values.password}
                          placeholder='Enter Password'
                          className={generateClassName(errors, touched, 'password')}
                          onChange={handleChange}
                          onBlur={() => setFieldTouched('password', true)}
                          disabled={isLoading}
                        />
                        <Form.Label htmlFor='password'>
                          Enter Password <span className='text-danger'>*</span>
                        </Form.Label>
                        {errors.password && touched.password && (
                          <div className='form_errors text-danger mt-1'>&#9888; {errors.password}</div>
                        )}
                      </div>
                    </Col>
                  </Row>

                  {currentError && (
                    <div className='text-danger mb-3'>
                      {typeof currentError === 'string' ? currentError : 'Login failed'}
                    </div>
                  )}

                  <button
                    type='submit'
                    className='w-100 text-center btn btn-dark py-2'
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Spinner as='span' animation='border' size='sm' className='me-2' />
                        Logging in...
                      </>
                    ) : (
                      <span>Login as {userType === 'partner' ? 'Partner' : 'Operations'}</span>
                    )}
                  </button>
                </Form>
              )}
            </Formik>
          ) : (
            
            /* --- OTP Form --- */
            <Formik
              initialValues={{ phone: '', otp: '' }}
              validationSchema={otpSchema}
              onSubmit={async (values, { setSubmitting }) => {
                setSubmitting(true);
                setOtpError('');

                if (!otpSent) {
                  try {
                    const payload = {
                      phone: values.phone,
                      otp: values.otp
                    };
                    let endpoint = userType === 'field_officer' ? '/field-officer/sendOTP' : '/sendOTP';
                    
                    const { data } = await axiosInstance.post(
                      endpoint, 
                      payload, 
                      { headers: { Authorization: btoa(`${values.phone}`) } }
                    );

                    if (data.success || data.description || data.msg === "OTP sent successfully") {
                      setOtpSent(true);
                      setDescription(data.description);
                    }
                  } catch (err) {
                    setOtpError(err.response?.data?.msg || err.message || 'Failed to send OTP');
                  }
                } else {
                  const base64PhoneAndOtp = btoa(`${values.phone}:${values.otp}`);
                  const loginPayload = { 
                    base64PhoneAndOtp, 
                    description, 
                    otp: values.otp 
                  };

                  if (userType === 'field_officer') {
                    dispatch(fieldOfficerLogin(loginPayload));
                  } else {
                    dispatch(farmerLogin(loginPayload));
                  }
                }
                setSubmitting(false);
              }}
            >
              {({ values, handleChange, handleSubmit, errors, touched, isSubmitting }) => (
                <Form onSubmit={handleSubmit} onChange={handleChange}>
                  <Row className='mb-3'>
                    <Col md={12}>
                      <div className='form-floating mb-1'>
                        {/* CHANGED: type='tel' to allow the '+' symbol */}
                        <Field
                          type='tel'
                          name='phone'
                          id='phone'
                          value={values.phone}
                          placeholder='Enter Phone Number'
                          className={generateClassName(errors, touched, 'phone')}
                          onChange={handleChange}
                          disabled={otpSent}
                        />
                        <Form.Label htmlFor='phone'>
                          Mobile Number <span className='text-danger'>*</span>
                        </Form.Label>
                      </div>
                      {/* NEW: Helper Text for global numbers */}
                      <div className="text-muted small mb-3 ms-1">
                        Enter country code first (e.g., +1) if not an Indian number.
                      </div>
                      {errors.phone && touched.phone && (
                        <div className='form_errors text-danger mb-3'>&#9888; {errors.phone}</div>
                      )}
                    </Col>

                    {otpSent && (
                      <Col md={12}>
                        <div className='form-floating mb-3'>
                          <Field
                            type='number'
                            name='otp'
                            id='otp'
                            value={values.otp}
                            placeholder='Enter OTP'
                            className='form-control'
                            onChange={handleChange}
                          />
                          <Form.Label htmlFor='otp'>
                            One Time Password <span className='text-danger'>*</span>
                          </Form.Label>
                          {errors.otp && touched.otp && (
                            <div className='form_errors text-danger mt-1'>&#9888; {errors.otp}</div>
                          )}
                        </div>
                      </Col>
                    )}
                  </Row>

                  {currentError && (
                    <div className='text-danger mb-3'>
                      {typeof currentError === 'string' ? currentError : 'An error occurred'}
                    </div>
                  )}

                  <button
                    type='submit'
                    className='w-100 text-center btn btn-dark py-2 mb-2'
                    disabled={
                      isLoading ||
                      isSubmitting ||
                      (!otpSent && !values.phone)
                    }
                  >
                    {isLoading || isSubmitting ? (
                      <>
                        <Spinner as='span' animation='border' size='sm' className='me-2' />
                        Processing...
                      </>
                    ) : (
                      <span>{otpSent ? 'Verify OTP' : 'Send OTP'}</span>
                    )}
                  </button>

                  {otpSent && (
                    <div className='d-flex justify-content-between'>
                      <button
                        type='button'
                        className='btn btn-outline-dark w-25'
                        onClick={() => {
                          setOtpSent(false);
                          setOtpError('');
                        }}
                      >
                        Reset
                      </button>
                      <button
                        type='button'
                        className='btn btn-outline-dark w-50'
                        onClick={() => {
                          setOtpSent(false);
                          setOtpError('');
                        }}
                      >
                        Change Number
                      </button>
                    </div>
                  )}
                </Form>
              )}
            </Formik>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;